import { gql, useQuery } from "@apollo/client";
import { Grid, LinearProgress } from "@mui/material";
import { ErrorBox } from "components/error";
import { SolutionPermission } from "features/access-management";

import { getConfigValue } from "utils/config";
import SpaceCard from "./SpaceCard";
import { useSolutionKey } from "./hooks/useSolutionKeyHook";
import {
  SolutionFeatures,
  SolutionFeaturesVariables
} from "./schema/SolutionFeatures";
import serviceProductsImage from "./solutionViewIcons/service-products.svg";
import usageMetricsImage from "./solutionViewIcons/usage-metrics.svg";
import useCaseDesignerImage from "./solutionViewIcons/use-case-designer.svg";
import useCasesImage from "./solutionViewIcons/use-cases.svg";
import membersImage from "./solutionViewIcons/users.svg";

const querySolutionFeatures = gql`
  query SolutionFeatures($solutionKey: Key!) {
    solutions {
      solution(solutionKey: $solutionKey) {
        key
        features {
          projects
          useCases
        }
      }
    }
  }
`;

export default function SolutionView() {
  const solutionKey = useSolutionKey();
  const { data, loading, error } = useQuery<
    SolutionFeatures,
    SolutionFeaturesVariables
  >(querySolutionFeatures, {
    variables: { solutionKey }
  });

  const features = data?.solutions?.solution?.features;
  const appStreamEnabled = getConfigValue("APPSTREAM_ENABLED");

  if (loading) return <LinearProgress />;
  if (error) {
    return <ErrorBox apolloError={error} />;
  }
  return (
    <Grid container spacing={4}>
      {appStreamEnabled !== "false" && (
        <Grid item>
          <Grid container spacing={2}>
            <Grid item>
              <SpaceCard
                name="Projects"
                requiredPermissions={[SolutionPermission.ReadProjects]}
                image={useCaseDesignerImage}
                description="Design web services with graphical programming inside the supply chain suite. Use uploaded data to make calculations and analyze the results."
                targetRoles={["SCS modeler", "customer"]}
                link={`/solutions/${solutionKey}/projects`}
                testid="projects-button"
                enabled={features?.projects}
              />
            </Grid>
          </Grid>
        </Grid>
      )}
      <Grid item>
        <Grid container spacing={2}>
          <Grid item>
            <SpaceCard
              name="Web services"
              requiredPermissions={[SolutionPermission.ReadWebServices]}
              image={useCasesImage}
              description="Upload and manage your web services, including testing, provisioning input data, and inspecting web service contracts."
              targetRoles={["customer"]}
              link={`/solutions/${solutionKey}/use-cases`}
              testid="use-cases-button"
              enabled={features?.useCases}
            />
          </Grid>
          <Grid item>
            <SpaceCard
              name="Service products"
              requiredPermissions={[SolutionPermission.ReadServiceProducts]}
              image={serviceProductsImage}
              description="Manage service products to control customer access to web services via API keys, including transaction limits and expiration dates."
              targetRoles={["Admin"]}
              link={`/solutions/${solutionKey}/service-products`}
              testid="service-products-button"
              enabled={features?.useCases}
            />
          </Grid>
          <Grid item>
            <SpaceCard
              name="Usage metrics"
              image={usageMetricsImage}
              description="Monitor web service transactions and export data for detailed analysis, such as tracking transactions per API key."
              targetRoles={["Admin"]}
              link={`/solutions/${solutionKey}/usage-metrics`}
              testid="usage-metrics-button"
              requiredPermissions={[
                SolutionPermission.ReadTransactions,
                SolutionPermission.ReadServiceProducts
              ]}
              enabled={features?.useCases}
            />
          </Grid>
        </Grid>
      </Grid>
      <Grid item>
        <Grid container spacing={2}>
          <Grid item>
            <SpaceCard
              name="Members"
              requiredPermissions={[SolutionPermission.ReadSolutionMembers]}
              image={membersImage}
              description="Manage solution members and member permissions."
              targetRoles={["Project leader"]}
              link={`/solutions/${solutionKey}/members`}
              testid="members-button"
            />
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
}
